import { useEffect } from 'react';

type WakeLockCapableNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinel>;
  };
};

/**
 * Hold a screen Wake Lock while `active` is true.
 *
 * The browser only permits wake locks while the document is visible. If the
 * user tabs away the OS revokes the lock automatically; we re-acquire on
 * `visibilitychange` once the document becomes visible again.
 *
 * No-op on browsers without `navigator.wakeLock` (Firefox, older Safari) and
 * when `active` is false.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const nav = navigator as WakeLockCapableNavigator;
    if (!nav.wakeLock) return;

    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const acquire = async () => {
      if (released || sentinel) return;
      try {
        sentinel = await nav.wakeLock!.request('screen');
        sentinel.addEventListener('release', () => {
          // OS (or visibility change) dropped the lock — forget the sentinel so
          // the next visibility check re-acquires.
          sentinel = null;
        });
      } catch {
        // Permission denied, no user gesture, or unsupported surface; ignore.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const toRelease = sentinel;
      sentinel = null;
      if (toRelease) void toRelease.release();
    };
  }, [active]);
}
